import { Suspense, useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Box, Sphere, Line } from '@react-three/drei';
import * as THREE from 'three';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { Database, Server, HardDrive, Shield, Zap, BarChart } from 'lucide-react';

const DatabaseNode = ({ position, color, label, size = 1 }) => {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.3;
    }
  });

  return (
    <group position={position}>
      <Box ref={meshRef} args={[size, size, size]}>
        <meshStandardMaterial color={color} />
      </Box>
      <Text
        position={[0, size + 0.5, 0]}
        fontSize={0.25}
        color="#1e3a8a"
        anchorX="center"
        anchorY="middle"
      >
        {label}
      </Text>
    </group>
  );
};

const ConnectionLine = ({ start, end }) => {
  const points = useMemo(() => [
    new THREE.Vector3(...start),
    new THREE.Vector3(...end)
  ], [start, end]);

  return (
    <Line
      points={points}
      color="#22d3ee"
      lineWidth={1}
    />
  );
};

const DataFlow = ({ start, end, speed = 1 }) => {
  const sphereRef = useRef();
  const progress = useRef(0);

  useFrame((state) => {
    if (sphereRef.current) {
      progress.current = (Math.sin(state.clock.elapsedTime * speed) + 1) / 2;
      const [sx, sy, sz] = start;
      const [ex, ey, ez] = end;
      sphereRef.current.position.x = sx + (ex - sx) * progress.current;
      sphereRef.current.position.y = sy + (ey - sy) * progress.current;
      sphereRef.current.position.z = sz + (ez - sz) * progress.current;
    }
  });

  return (
    <Sphere ref={sphereRef} args={[0.08]}>
      <meshStandardMaterial color="#67e8f9" emissive="#67e8f9" emissiveIntensity={0.3} />
    </Sphere>
  );
};

const DatabaseArchitecture = () => {
  const nodes = useMemo(() => [
    { position: [0, 0, 0], color: "#1e3a8a", label: "Main DB", size: 1.2 },
    { position: [-2.5, 1, 0], color: "#22d3ee", label: "Cache", size: 0.8 },
    { position: [2.5, 1, 0], color: "#06b6d4", label: "Replica", size: 0.8 },
    { position: [0, -1.5, 0], color: "#67e8f9", label: "Analytics", size: 0.8 },
    { position: [-1.5, -1, 1.5], color: "#22d3ee", label: "Backup", size: 0.6 },
    { position: [1.5, -1, 1.5], color: "#06b6d4", label: "Archive", size: 0.6 },
  ], []);

  const connections = useMemo(() => [
    { start: [0, 0, 0], end: [-2.5, 1, 0] },
    { start: [0, 0, 0], end: [2.5, 1, 0] },
    { start: [0, 0, 0], end: [0, -1.5, 0] },
    { start: [0, 0, 0], end: [-1.5, -1, 1.5] },
    { start: [0, 0, 0], end: [1.5, -1, 1.5] },
  ], []);

  const dataFlows = useMemo(() => [
    { start: [0, 0, 0], end: [-2.5, 1, 0], speed: 1.2 },
    { start: [0, 0, 0], end: [2.5, 1, 0], speed: 0.8 },
    { start: [0, 0, 0], end: [0, -1.5, 0], speed: 1.5 },
  ], []);

  return (
    <>
      <ambientLight intensity={0.4} />
      <pointLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, -5, -5]} intensity={0.3} color="#22d3ee" />
      
      {nodes.map((node, index) => (
        <DatabaseNode
          key={index}
          position={node.position}
          color={node.color}
          label={node.label}
          size={node.size}
        />
      ))}
      
      {connections.map((connection, index) => (
        <ConnectionLine
          key={index}
          start={connection.start}
          end={connection.end}
        />
      ))}
      
      {dataFlows.map((flow, index) => (
        <DataFlow
          key={index}
          start={flow.start}
          end={flow.end}
          speed={flow.speed}
        />
      ))}
      
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        enableRotate={true}
        autoRotate={true}
        autoRotateSpeed={0.3}
        maxDistance={12}
        minDistance={6}
      />
    </>
  );
};

const LoadingFallback = ({ isDark }) => (
  <div className={`w-full h-96 ${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-900 to-azellar-navy'} rounded-2xl flex items-center justify-center`}>
    <div className="text-white text-center">
      <div className="animate-spin w-12 h-12 border-4 border-azellar-teal border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-sm opacity-75">Loading 3D Visualization...</p>
    </div>
  </div>
);

// Fallback 2D Animation Component
const FallbackAnimation = ({ isDark }) => {
  const [activeNode, setActiveNode] = useState(0);

  const nodes = [
    { id: 'main', icon: Database, label: 'Main Database', color: 'text-blue-500', position: 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' },
    { id: 'cache', icon: Zap, label: 'Cache Layer', color: 'text-cyan-500', position: 'top-1/4 left-1/4' },
    { id: 'replica', icon: Server, label: 'Read Replica', color: 'text-teal-500', position: 'top-1/4 right-1/4' },
    { id: 'analytics', icon: BarChart, label: 'Analytics DB', color: 'text-cyan-400', position: 'bottom-1/4 left-1/2 transform -translate-x-1/2' },
    { id: 'backup', icon: Shield, label: 'Backup Storage', color: 'text-cyan-300', position: 'bottom-1/4 left-1/4' },
    { id: 'archive', icon: HardDrive, label: 'Archive Storage', color: 'text-teal-300', position: 'bottom-1/4 right-1/4' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveNode((prev) => (prev + 1) % nodes.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [nodes.length]);

  return (
    <div className={`w-full h-96 ${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-900 to-azellar-navy'} rounded-2xl overflow-hidden relative p-8`}>
      {/* Connection Lines */}
      <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#22d3ee', stopOpacity: 0.3 }} />
            <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0.8 }} />
          </linearGradient>
        </defs>
        <line x1="50%" y1="50%" x2="25%" y2="25%" stroke="url(#lineGradient)" strokeWidth="2" />
        <line x1="50%" y1="50%" x2="75%" y2="25%" stroke="url(#lineGradient)" strokeWidth="2" />
        <line x1="50%" y1="50%" x2="50%" y2="75%" stroke="url(#lineGradient)" strokeWidth="2" />
        <line x1="50%" y1="50%" x2="25%" y2="75%" stroke="url(#lineGradient)" strokeWidth="2" />
        <line x1="50%" y1="50%" x2="75%" y2="75%" stroke="url(#lineGradient)" strokeWidth="2" />
      </svg>

      {/* Database Nodes */}
      {nodes.map((node, index) => {
        const Icon = node.icon;
        const isActive = index === activeNode;
        return (
          <motion.div
            key={node.id}
            className={`absolute ${node.position} z-10`}
            animate={{
              scale: isActive ? [1, 1.2, 1] : 1,
              rotate: isActive ? [0, 360] : 0,
            }}
            transition={{
              scale: { duration: 0.6, ease: "easeInOut" },
              rotate: { duration: 2, ease: "linear" },
            }}
          >
            <div className="relative group cursor-pointer">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isActive ? 'bg-gradient-to-r from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/50' : 'bg-gray-800 border border-gray-600'} transition-all duration-300`}>
                <Icon className={`w-8 h-8 ${isActive ? 'text-white' : node.color}`} />
              </div>
              
              {/* Pulse Animation */}
              {isActive && (
                <div className="absolute inset-0 w-16 h-16 rounded-full bg-cyan-500 opacity-20 animate-ping"></div>
              )}
              
              {/* Tooltip */}
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                {node.label}
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Data Flow Animation */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-2 h-2 bg-cyan-400 rounded-full transform -translate-x-1/2 -translate-y-1/2"
        animate={{
          x: [0, -100, 100, 0, -100, 100, 0],
          y: [0, -100, -100, 100, 100, 0, 0],
          scale: [0.5, 1, 0.5, 1, 0.5, 1, 0.5],
        }}
        transition={{
          duration: 8,
          ease: "linear",
          repeat: Infinity,
        }}
      />

      <div className="absolute bottom-4 left-4 text-white z-10">
        <p className="text-sm opacity-75">Database Architecture Visualization</p>
        <p className="text-xs opacity-50">Interactive nodes show different database components</p>
      </div>
    </div>
  );
};

const DatabaseVisualization = () => {
  const { isDark } = useTheme();
  const [use3D, setUse3D] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Error boundary for 3D canvas
  useEffect(() => {
    const handleError = () => {
      console.log('3D rendering failed, falling back to 2D animation');
      setUse3D(false);
      setHasError(true);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // If there was an error or 3D is disabled, show 2D fallback
  if (!use3D || hasError) {
    return <FallbackAnimation isDark={isDark} />;
  }

  return (
    <div className={`w-full h-96 ${isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-900 to-azellar-navy'} rounded-2xl overflow-hidden relative`}>
      <Suspense 
        fallback={<LoadingFallback isDark={isDark} />}
      >
        <Canvas 
          camera={{ position: [6, 4, 6], fov: 50 }}
          performance={{ min: 0.5 }}
          dpr={[1, 2]}
          onError={() => {
            console.log('Canvas error, switching to 2D fallback');
            setUse3D(false);
          }}
          gl={{ 
            antialias: true, 
            alpha: true,
            powerPreference: "high-performance"
          }}
        >
          <DatabaseArchitecture />
        </Canvas>
      </Suspense>
      
      <div className="absolute bottom-4 left-4 text-white z-10">
        <p className="text-sm opacity-75">Interactive 3D Database Architecture</p>
        <p className="text-xs opacity-50">Drag to rotate • Scroll to zoom</p>
      </div>
      
      {/* Fallback button */}
      <button 
        onClick={() => setUse3D(false)}
        className="absolute top-4 right-4 text-white/50 hover:text-white text-xs bg-black/20 px-2 py-1 rounded transition-colors"
      >
        Switch to 2D
      </button>
    </div>
  );
};

export default DatabaseVisualization;